import { usePubSub } from "@videosdk.live/react-sdk";
import { useAppContext } from "../AppContextDef";

/**
 * Client-side pubsub subscriber. The recorder now owns the verification
 * pipeline and is the sole publisher of the verdict channels, so the client
 * must subscribe to them to drive its own ParticipantView UI. It also listens
 * to DELEGATE_API to know when the recorder asks it to run a single API as a
 * bandwidth fallback.
 */
const ClientPubSubHandler = () => {
  const {
    setFaceVerified,
    setAgreedData,
    setFaceDetectError,
    setIsAbortSignal,
    setDelegatedApi,
    setIsRecorderNetworkUnstable,
  } = useAppContext();

  const latest = (messages) =>
    messages.sort((a, b) => {
      if (a.timestamp > b.timestamp) return -1;
      if (a.timestamp < b.timestamp) return 1;
      return 0;
    })[0];

  usePubSub("FACE_VERIFIED", {
    onMessageReceived: ({ payload }) => {
      setFaceVerified({ isVerified: payload.isVerified, timestamp: Date.now() });
    },
    onOldMessagesReceived: (messages) => {
      const m = latest(messages);
      if (m)
        setFaceVerified({
          isVerified: m.payload.isVerified,
          timestamp: Date.now(),
        });
    },
  });

  usePubSub("AGREED", {
    onMessageReceived: ({ payload }) => {
      setAgreedData(payload.isAgreed);
    },
    onOldMessagesReceived: (messages) => {
      const m = latest(messages);
      if (m) setAgreedData(m.payload.isAgreed);
    },
  });

  usePubSub("FACE_DETECT_ERROR", {
    onMessageReceived: ({ payload }) => {
      setFaceDetectError({ err: payload.err, timestamp: Date.now() });
    },
    onOldMessagesReceived: (messages) => {
      const m = latest(messages);
      if (m) setFaceDetectError({ err: m.payload.err, timestamp: Date.now() });
    },
  });

  usePubSub("ABORT_SIGNAL", {
    onMessageReceived: ({ payload }) => {
      setIsAbortSignal(payload.isAbort);
    },
    onOldMessagesReceived: (messages) => {
      const m = latest(messages);
      if (m) setIsAbortSignal(m.payload.isAbort);
    },
  });

  usePubSub("DELEGATE_API", {
    onMessageReceived: ({ payload }) => {
      setDelegatedApi({ ...payload, timestamp: Date.now() });
    },
    onOldMessagesReceived: (messages) => {
      const m = latest(messages);
      if (m) setDelegatedApi({ ...m.payload, timestamp: Date.now() });
    },
  });

  // Recorder-observed network instability (customer's incoming video bitrate at
  // ~0). Mirrors the recorder's flag so the client can show the blocking dialog
  // and stop/disable the consent recording.
  usePubSub("NETWORK_UNSTABLE", {
    onMessageReceived: ({ payload }) => {
      setIsRecorderNetworkUnstable(!!payload?.isUnstable);
    },
    onOldMessagesReceived: (messages) => {
      const m = latest(messages);
      if (m) setIsRecorderNetworkUnstable(!!m.payload?.isUnstable);
    },
  });

  return <></>;
};

export default ClientPubSubHandler;
